import { useId } from 'react';
import { CREAM, INK, MARK_DOTS, MARK_OFFSET } from '../brand/GromlyMark';

// Brand wordmark for the AI copilot: "g" in the surrounding text colour, "IA"
// in Gromly teal. Inherits font size/weight from its parent (used inside the
// panel header and, uppercased, in the collapsed rail). Keep in sync with the
// plain-string name in i18n (`ai.name`) used for title/aria attributes.
export function GiaName({ className }: { className?: string }) {
  return (
    <span className={className}>
      g<span className="text-teal-500 dark:text-teal-400">IA</span>
    </span>
  );
}

// Brand icon for the AI copilot ("gIA"): Gromly's mark on an ink disc, with
// the destination dot turned into a teal sparkle badge. Inlined so it scales
// crisply and carries its own colours (no theming). The gradient id is
// per-instance (useId) so multiple renders don't collide.
export function GiaLogo({ size = 32, className }: { size?: number; className?: string }) {
  const gradId = useId();
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className} role="img" aria-hidden>
      <circle cx="50" cy="50" r="48" fill={INK} />
      <g transform="translate(12,20) scale(0.72)">
        <g transform={MARK_OFFSET}>
          {MARK_DOTS.map(d => <circle key={d.cx} {...d} fill={CREAM} />)}
        </g>
      </g>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2dd4bf" />
          <stop offset="100%" stopColor="#0d9488" />
        </linearGradient>
      </defs>
      <circle cx="66.7" cy="39.4" r="13.5" fill={`url(#${gradId})`} />
      <g transform="translate(59.5,32.2) scale(0.6)">
        <path
          d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"
          fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

// The logo wrapped in a teal "alive" effect: two expanding radar rings (a clear
// pulse) over a soft breathing halo. Honours prefers-reduced-motion — the
// animated layers use motion-safe:*, so with reduced motion only a static halo
// remains.
export function GiaLogoGlow({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <span className={`relative inline-flex items-center justify-center ${className ?? ''}`}>
      {/* Soft breathing halo (fallback when motion is reduced). */}
      <span
        aria-hidden
        className="absolute -inset-1 rounded-full bg-teal-400/60 blur-md motion-safe:animate-gia-glow motion-reduce:opacity-50"
      />
      {/* Expanding radar rings — the clearly-visible pulse. Second is staggered. */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full border-2 border-teal-400 opacity-0 motion-safe:animate-gia-ping"
      />
      <span
        aria-hidden
        className="absolute inset-0 rounded-full border-2 border-teal-400 opacity-0 motion-safe:animate-gia-ping [animation-delay:1.1s]"
      />
      <GiaLogo size={size} className="relative" />
    </span>
  );
}
